import { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { X, Bot, PartyPopper } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { createPageUrl } from '@/components/utils';
import AssistantMessage from './MessageMarkdown';
import InlineMiniChart from './InlineMiniChart';
import AIComposer from './AIComposer';
import AiCreditsMeter from '@/components/plan/AiCreditsMeter';
import PendingActionsTray from '@/components/ai/mission-control/PendingActionsTray';
import { useAriaConversation } from '@/hooks/useAriaConversation';
import useStickToBottom from '@/components/ai/hooks/useStickToBottom';
import { domainForModule, domainTokens } from '@/components/ai/mission-control/ariaDomainTokens';
import ImportFileChips from './ImportFileChips';
import { partitionFiles, channelForFile } from '@/lib/ariaAcceptedFiles';
import { uploadTableFile } from '@/lib/uploadTableFile';
import { extractWordText } from '@/lib/extractWordText';
import { toast } from 'sonner';
import { useInvoiceFormContext } from '@/lib/invoiceFormContext';

// Human-readable tool name for the reasoning strip (forecast_cash_flow → "Forecast cash flow").
function toolLabel(tool) {
  if (!tool) return '';
  return tool.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim().replace(/^./, (c) => c.toUpperCase());
}

// Keys correspond to the activeKey prop passed by Layout/TopBar (currentPageName).
const SUGGESTIONS_BY_PAGE = {
  Home: [
    { title: 'briefing', body: 'briefing_body' },
    { title: 'cashflow_forecast', body: 'cashflow_forecast_body' },
    { title: 'explain_kpi', body: 'explain_kpi_body' },
  ],
  Facturas: [
    { title: 'invoice_draft', body: 'invoice_draft_body' },
    { title: 'invoice_reminders', body: 'invoice_reminders_body' },
    { title: 'detect_anomalies', body: 'detect_anomalies_body' },
  ],
  FacturaForm: [
    { title: 'complete_invoice', body: 'complete_invoice_body', prompt: 'complete_invoice_prompt' },
    { title: 'invoice_due_date', body: 'invoice_due_date_body', prompt: 'invoice_due_date_prompt' },
    { title: 'invoice_taxes', body: 'invoice_taxes_body', prompt: 'invoice_taxes_prompt' },
  ],
  RRHHEmpleados: [
    { title: 'add_employee', body: 'add_employee_body', prompt: 'add_employee_prompt' },
    { title: 'organize_team', body: 'organize_team_body', prompt: 'organize_team_prompt' },
    { title: 'employee_data', body: 'employee_data_body', prompt: 'employee_data_prompt' },
  ],
  Projects: [
    { title: 'first_project', body: 'first_project_body', prompt: 'first_project_prompt' },
    { title: 'prioritize_tasks', body: 'prioritize_tasks_body', prompt: 'prioritize_tasks_prompt' },
    { title: 'project_status', body: 'project_status_body', prompt: 'project_status_prompt' },
  ],
  Bancos: [
    { title: 'bank_unreconciled', body: 'bank_unreconciled_body', prompt: 'bank_unreconciled_prompt' },
    { title: 'bank_import', body: 'bank_import_body', prompt: 'bank_import_prompt' },
    { title: 'bank_balance', body: 'bank_balance_body', prompt: 'bank_balance_prompt' },
  ],
  Impuestos: [
    { title: 'review_303', body: 'review_303_body' },
    { title: 'fiscal_calendar', body: 'fiscal_calendar_body' },
    { title: 'detected_deductions', body: 'detected_deductions_body' },
  ],
  CRMPipeline: [
    { title: 'pipeline_summary', body: 'pipeline_summary_body' },
    { title: 'draft_email', body: 'draft_email_body' },
    { title: 'lead_score', body: 'lead_score_body' },
  ],
  Nominas: [
    { title: 'payroll_ss_cost', body: 'payroll_ss_cost_body' },
    { title: 'payroll_irpf_check', body: 'payroll_irpf_check_body' },
    { title: 'payroll_contracts', body: 'payroll_contracts_body' },
  ],
  _default: [
    { title: 'ask_screen', body: 'ask_screen_body' },
    { title: 'generate_action', body: 'generate_action_body' },
    { title: 'company_search', body: 'company_search_body' },
  ],
};

const INVOICE_REQUIREMENTS_FISCAL = new Set(['nif', 'tipo_linea', 'causa_exencion', 'cuenta_ingreso']);

function invoiceFormSuggestions(context) {
  if (!context) return SUGGESTIONS_BY_PAGE.FacturaForm;
  if (context.readOnly) return [
    { title: 'inspect_issued_invoice', body: 'inspect_issued_invoice_body', prompt: 'inspect_issued_invoice_prompt' },
    SUGGESTIONS_BY_PAGE.FacturaForm[2],
  ];

  const missing = new Set(context.missing);
  const suggestions = [];
  if (missing.has('cliente')) suggestions.push({ title: 'invoice_customer', body: 'invoice_customer_body', prompt: 'invoice_customer_prompt' });
  if (missing.has('vencimiento')) suggestions.push(SUGGESTIONS_BY_PAGE.FacturaForm[1]);
  if (missing.has('linea')) suggestions.push({ title: 'invoice_line', body: 'invoice_line_body', prompt: 'invoice_line_prompt' });
  if ([...missing].some((id) => INVOICE_REQUIREMENTS_FISCAL.has(id))) suggestions.push(SUGGESTIONS_BY_PAGE.FacturaForm[2]);
  if (suggestions.length === 0 && missing.size === 0) return [
    { title: 'review_invoice', body: 'review_invoice_body', prompt: 'review_invoice_prompt' },
    SUGGESTIONS_BY_PAGE.FacturaForm[2],
  ];
  if (suggestions.length < 3) suggestions.push(SUGGESTIONS_BY_PAGE.FacturaForm[0]);
  if (suggestions.length < 3 && !suggestions.some((s) => s.title === 'invoice_taxes')) suggestions.push(SUGGESTIONS_BY_PAGE.FacturaForm[2]);
  if (suggestions.length < 3) suggestions.push({ title: 'review_invoice', body: 'review_invoice_body', prompt: 'review_invoice_prompt' });
  return suggestions.slice(0, 3);
}

const PAGE_LABELS = {
  Home: 'home',
  Facturas: 'invoices',
  FacturaForm: 'invoice_form',
  RRHHEmpleados: 'team',
  Projects: 'projects',
  Bancos: 'banks',
  Impuestos: 'taxes',
  CRMPipeline: 'crm',
  Nominas: 'payroll',
};

export default function AIPanel({ open, onClose, activeKey = 'Home', celebrateSetup = false }) {
  const { t } = useTranslation();
  const invoiceFormContext = useInvoiceFormContext();
  const suggestions = activeKey === 'FacturaForm'
    ? invoiceFormSuggestions(invoiceFormContext)
    : (SUGGESTIONS_BY_PAGE[activeKey] || SUGGESTIONS_BY_PAGE._default);
  // Mission Control visual language: tint the widget to the active module's
  // domain so the floating panel and the full canvas read as one system.
  const tokens = domainTokens(domainForModule(activeKey));

  // Shared conversation store — the widget renders the SAME thread as the
  // full-page assistant and survives navigation + reload.
  const {
    messages, isLoading, activeTool, sendMessage, ensureLoaded,
    pendingActions, confirmAction, rejectAction,
  } = useAriaConversation();
  const navigate = useNavigate();
  const lastEvaluatedMsgId = useRef(null);
  const [pendingRedirect, setPendingRedirect] = useState(false);
  // El hilo no bajaba al enviar: se quedaba arriba y el usuario no veía ni su
  // propio mensaje. El patrón es el mismo que el del canvas, ahora compartido.
  const { scrollContainerRef, handleScroll } = useStickToBottom(messages);
  // Deep-link desde una chip de «Fuentes». Estable, para no romper la
  // memoización de `AssistantMessage`.
  const handleSourceClick = useCallback((route) => {
    if (!route) return;
    navigate(route);
    onClose?.();
  }, [navigate, onClose]);

  // ── Adjuntos ──────────────────────────────────────────────────────
  //
  // El system prompt le promete el clip a ARIA en todas las superficies, y este
  // panel —el que se usa en TODA la app— no lo tenia: ARIA pedia adjuntar un
  // fichero que no habia forma de adjuntar.
  //
  // Dos canales, los dos de estado plano: Excel/CSV (que viaja como `import_files`
  // y lo procesa la tool `importar_tabla`) y Word (texto inline en `word_docs`).
  //
  // El OCR de PDF e imagenes NO esta aqui a proposito: su sondeo dura hasta tres
  // minutos y este panel se DESMONTA al cerrarlo (`if (!open) return null`), asi
  // que la tanda se perderia a medias. Esos documentos tienen su sitio —la Bandeja—
  // y ahi se manda al usuario, que es mejor que tragarselos en silencio.
  const [tableFiles, setTableFiles] = useState([]);
  const [wordTexts, setWordTexts] = useState([]);

  const handleRemoveFile = useCallback((id) => {
    setTableFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const handleAddFiles = useCallback(async (files) => {
    const { accepted, rejected } = partitionFiles(files);

    for (const r of rejected) {
      toast.error(r.reason === 'size'
        ? t('ai.file_too_large', {
          defaultValue: '"{{name}}" supera el limite de {{mb}} MB.',
          name: r.file.name,
          mb: Math.round((r.maxBytes || 0) / (1024 * 1024)),
        })
        : t('ai.file_unsupported', {
          defaultValue: '"{{name}}": ARIA no sabe leer ese formato.',
          name: r.file.name,
        }));
    }

    for (const file of accepted) {
      const channel = channelForFile(file);

      if (channel === 'ocr') {
        // Un PDF soltado aqui no se queda en el limbo: se dice donde se procesa.
        toast.info(t('ai.doc_goes_to_inbox', {
          defaultValue: '"{{name}}" se procesa en la Bandeja de documentos.',
          name: file.name,
        }), {
          action: {
            label: t('ai.go_to_inbox', { defaultValue: 'Ir a la Bandeja' }),
            onClick: () => navigate('/bandeja-documentos'),
          },
        });
        continue;
      }

      if (channel === 'word') {
        try {
          const text = await extractWordText(file);
          setWordTexts((prev) => [...prev, { id: `${Date.now()}-${file.name}`, name: file.name, text }]);
          toast.success(t('ai.word_attached', {
            defaultValue: '"{{name}}" adjuntado — ARIA leera su contenido.',
            name: file.name,
          }));
        } catch (err) {
          toast.error(err?.message || t('ai.word_read_error', { defaultValue: 'No se pudo leer el documento Word' }));
        }
        continue;
      }

      const localId = `${Date.now()}-${file.name}-${Math.round(file.size)}`;
      setTableFiles((prev) => [...prev, { id: localId, filename: file.name, status: 'uploading' }]);
      try {
        const meta = await uploadTableFile(file);
        setTableFiles((prev) => prev.map((x) => (x.id === localId ? { ...x, ...meta, status: 'ready' } : x)));
      } catch (err) {
        setTableFiles((prev) => prev.map((x) => (x.id === localId ? { ...x, status: 'error', error: err?.message } : x)));
        toast.error(err?.message || t('ai.upload_file_error', { defaultValue: 'No se pudo subir el fichero' }));
      }
    }
  }, [navigate, t]);

  // Watch for messages with charts or tables to prompt for full screen expansion
  useEffect(() => {
    if (!open || !messages.length) return;
    
    const lastMsg = messages[messages.length - 1];
    
    if (lastMsg && lastMsg.role !== 'user' && !lastMsg.isThinking && lastEvaluatedMsgId.current !== lastMsg.id) {
      lastEvaluatedMsgId.current = lastMsg.id;
      const hasChart = !!lastMsg.chart;
      const hasTable = lastMsg.content && /\|\s*[:-]+(?:\s*\|\s*[:-]+)+\s*\|/.test(lastMsg.content);
      
      if (hasChart || hasTable) {
        setPendingRedirect(true);
      }
    }
  }, [messages, open]);

  // Hydrate the conversation the first time the widget is opened (idempotent —
  // the page does the same; only the first caller triggers the network load).
  useEffect(() => {
    if (open) ensureLoaded(activeKey ? String(activeKey).slice(0, 50) : '');
  }, [open, activeKey, ensureLoaded]);

  // Visible thread — drop the pre-token thinking placeholder; the busy strip
  // covers that moment. The store keeps the rich shape; map it to the widget's.
  const visible = messages.filter((m) => !m.isThinking);

  /**
   * Acciones que ARIA propuso desde este panel y siguen sin ejecutar.
   *
   * El panel comparte el store con la superficie completa, así que la acción
   * EXISTÍA en estado — pero aquí no se renderizaba ninguna superficie de
   * aprobación. ARIA pedía confirmación y no había botón: el usuario tenía que
   * adivinar que debía irse al Home para encontrarlo. Se reutiliza la misma
   * bandeja que el canvas (que a su vez reusa `PendingActionCard`), no una
   * tarjeta nueva.
   *
   * Sin "descartar": esa preferencia se persiste en el canvas por `msgId` y
   * duplicar aquí el almacenamiento sería otra fuente de verdad. Desde el panel
   * se aprueba o se rechaza, que es lo que desbloquea al usuario.
   */
  const pendingActionItems = Object.entries(pendingActions || {})
    .filter(([, p]) => p?.status === 'pending')
    .map(([id, p]) => ({ msgId: id, action: p.action, status: p.status }));
  const reasoning = toolLabel(activeTool);

  const suggestionPrompt = (suggestion) => {
    const prompt = t(`ai.suggestion.${suggestion.prompt || suggestion.title}`);
    if (activeKey !== 'FacturaForm' || !invoiceFormContext) return prompt;

    if (invoiceFormContext.readOnly) {
      return `${prompt}\n${t('ai.invoice_context_read_only')}`;
    }
    const missing = invoiceFormContext.missing
      .map((id) => t(`ai.invoice_requirements.${id}`, { defaultValue: '' }))
      .filter(Boolean);
    const status = missing.length
      ? t('ai.invoice_context_missing', { fields: missing.join(', ') })
      : t('ai.invoice_context_ready');
    return `${prompt}\n${status}\n${t('ai.invoice_context_caution')}`;
  };

  const send = async (text) => {
    // Solo los que acabaron de subir: uno en error no tiene `s3_key` que mandar.
    const importFiles = tableFiles
      .filter((f) => f.status === 'ready' && f.s3_key)
      .map((f) => ({ s3_key: f.s3_key, filename: f.filename, content_type: f.content_type }));
    const wordDocs = wordTexts.map((w) => ({ filename: w.name, text: w.text }));
    // Se limpian AL ENVIAR: son parte del mensaje, no una ingesta aparte.
    const sentTableFiles = tableFiles;
    const sentWordTexts = wordTexts;
    setTableFiles([]);
    setWordTexts([]);

    const result = await sendMessage(text, {
      module: activeKey ? String(activeKey).slice(0, 50) : undefined,
      ...(importFiles.length ? { import_files: importFiles } : {}),
      ...(wordDocs.length ? { word_docs: wordDocs } : {}),
    });

    // Si el turno no cuaja, los adjuntos vuelven. Un "Request aborted" de Bedrock
    // es transitorio —el backend mismo lo clasifica asi— pero obligar a re-adjuntar
    // convierte un tropiezo de segundos en empezar de cero. El fichero sigue en S3:
    // lo que se perdia era solo su referencia en pantalla.
    //
    // Se FUSIONA en vez de asignar porque el turno puede tardar hasta 27 s y el
    // usuario puede haber adjuntado otra cosa mientras: asignar le borraria lo
    // nuevo, que es el mismo bug al reves.
    if (result?.ok === false) {
      const restore = (sent) => (current) => [
        ...sent.filter((f) => !current.some((c) => c.id === f.id)),
        ...current,
      ];
      setTableFiles(restore(sentTableFiles));
      setWordTexts(restore(sentWordTexts));
    }
    return result;
  };

  if (!open) return null;

  return (
    <aside
      data-testid="ai-panel"
      role="complementary"
      aria-label={t('ai.panel_title')}
      /* < sm ocupa todo el ancho: 380px fijos dejaban el chat en una columna de
         ~10 caracteres sobre un móvil de 360px y tapaban la página igualmente. */
      className="fixed inset-x-0 sm:inset-x-auto sm:right-0 top-[3.75rem] bottom-0 w-full sm:w-[380px] max-w-full z-40 flex flex-col bg-card border-l border-border shadow-2xl lg-chrome lg-panel"
    >
      {/* Header */}
      <div className="flex items-center gap-2.5 px-4 py-3 border-b border-border">
        <div
          className="w-7 h-7 rounded-md flex items-center justify-center"
          style={{ background: `${tokens.hex}22`, color: tokens.hex, boxShadow: `0 0 12px ${tokens.glow}` }}
        >
          <Bot className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold leading-tight">{t('ai.panel_title')}</div>
          <div className="text-[11px] text-muted-foreground leading-tight mt-0.5 flex items-center gap-1.5">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-success" />
            <span>
              {t('ai.context_label')}: <span className="font-medium text-foreground">{PAGE_LABELS[activeKey] ? t(`ai.context_pages.${PAGE_LABELS[activeKey]}`) : t('ai.current_screen')}</span>
            </span>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="w-7 h-7"
          onClick={onClose}
          aria-label={t('ai.panel_close')}
          data-testid="ai-panel-close"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Saldo de créditos — justo bajo la cabecera, antes de la conversación:
          es lo que se gasta aquí, y hasta ahora solo se veía en Ajustes → Plan. */}
      <AiCreditsMeter />

      {/* Banda "wow" — solo cuando el panel se abrió automáticamente desde la
          celebración de onboarding (ver Layout.jsx, `celebrateAria`). Es capa
          visual encima del saludo normal de ARIA, no reemplaza su contenido. */}
      {celebrateSetup && (
        <div className="flex items-center gap-2 px-4 py-2 border-b border-border bg-primary/10 text-xs text-primary">
          <PartyPopper className="h-3.5 w-3.5 shrink-0" />
          <span>{t('ai.celebration_banner', '¡Enhorabuena! Acabas de completar tu configuración esencial.')}</span>
        </div>
      )}

      {/* Suggestions strip — only when conversation hasn't moved past the seed */}
      {visible.length <= 1 && (
        <div className="px-4 pt-3">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">
            {t('ai.suggestions_for_screen')}
          </div>
          <div className="grid gap-1.5">
            {suggestions.map((s) => (
              <button
                key={s.title}
                type="button"
                onClick={() => send(suggestionPrompt(s))}
                className="text-left px-3 py-2 rounded-md border border-border hover:bg-muted text-xs transition-colors"
              >
                <div className="font-medium text-foreground">{t(`ai.suggestion.${s.title}`)}</div>
                <div className="text-muted-foreground mt-0.5 line-clamp-2">{t(`ai.suggestion.${s.body}`)}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Conversation */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-4 py-3 space-y-3"
        role="log"
        aria-live="polite"
        aria-label={t('ai.conversation_log', { defaultValue: 'Historial de conversación' })}
      >
        {visible.map((m) => {
          const isAi = m.role !== 'user';
          return (
            <div key={m.id} data-message-id={isAi ? m.id : undefined} className={cn('flex flex-col gap-1', isAi ? 'items-start' : 'items-end')}>
              <div
                className={cn(
                  'max-w-[85%] px-3 py-2 rounded-lg text-sm leading-relaxed',
                  isAi ? 'bg-muted text-foreground' : 'bg-primary text-primary-foreground'
                )}
              >
                {isAi ? (
                  // Sin `sources`, `MessageMarkdown` cae a su fallback por regex
                  // sobre el texto y pinta el nombre CRUDO de la tool
                  // (`consultar_facturas`) donde el canvas pinta el departamento
                  // («Facturación»). El dato ya venía en el mensaje: solo hacía
                  // falta pasárselo. Al navegar se cierra el panel: el deep-link
                  // lleva a otra pantalla y dejarlo abierto encima la tapa.
                  <AssistantMessage
                    content={m.content}
                    sources={m.sources}
                    meta={m.sourceMeta}
                    onSourceClick={handleSourceClick}
                  />
                ) : m.content}
              </div>
              {isAi && m.chart && (
                <div className="w-full max-w-[85%]">
                  <InlineMiniChart chart={m.chart} />
                </div>
              )}
              {m.timestamp && <div className="text-[10px] text-muted-foreground font-mono">{m.timestamp}</div>}
            </div>
          );
        })}
        {isLoading && (
          <div
            className="flex items-center gap-2 rounded-full px-3 py-1.5 text-xs"
            style={{ background: `${tokens.hex}14`, color: tokens.hex }}
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60" style={{ background: tokens.hex }} />
              <span className="relative inline-flex h-2 w-2 rounded-full" style={{ background: tokens.hex }} />
            </span>
            <span className="font-medium">{reasoning ? `Consultando ${reasoning}…` : t('ai.thinking')}</span>
          </div>
        )}
      </div>

      {pendingActionItems.length > 0 && (
        <div className="shrink-0 border-t border-border px-4 py-3">
          <PendingActionsTray
            items={pendingActionItems}
            onApprove={confirmAction}
            onReject={rejectAction}
          />
        </div>
      )}

      {/* Composer + banner. El banner se ancla al composer (`bottom-full`) y no a
          un `bottom-[80px]` fijo: ese numero se calculo contra la altura de un
          composer sin adjuntos, y dejaba de valer en cuanto crecia con los chips. */}
      <div className="relative shrink-0">
      {/* Pending Redirect Approval Banner */}
      {pendingRedirect && (
        <div className="absolute bottom-full mb-2 left-4 right-4 bg-primary text-primary-foreground p-4 rounded-xl shadow-2xl z-50 flex flex-col gap-3 animate-in slide-in-from-bottom-2 fade-in">
          <p className="text-[13px] font-medium leading-relaxed">
            Esta respuesta incluye gráficos o desarrollo espacial extenso. ¿Abrir el entorno completo de Aria?
          </p>
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="ghost" className="h-8 px-3 text-primary-foreground/80 hover:text-primary-foreground hover:bg-primary-foreground/10" onClick={() => setPendingRedirect(false)}>
              Ver aquí
            </Button>
            <Button size="sm" variant="secondary" className="h-8 px-3 font-semibold shadow-sm" onClick={() => {
              setPendingRedirect(false);
              navigate(createPageUrl('AsistenteAgentic'));
              if (onClose) onClose();
            }}>
              Abrir panel
            </Button>
          </div>
        </div>
      )}
      {/* Composer — estado de texto propio (no re-renderiza el panel al teclear) */}
      <AIComposer
        onSend={send}
        disabled={isLoading}
        placeholder={t('ai.composer_placeholder')}
        onAddFiles={handleAddFiles}
        attachmentsSlot={tableFiles.length > 0 ? (
          <div className="max-h-[96px] overflow-y-auto px-3 pt-3">
            <ImportFileChips files={tableFiles} onRemove={handleRemoveFile} />
          </div>
        ) : null}
      />
      </div>
    </aside>
  );
}
